import React from 'react';
import { Box, Button, Flex, Modal, Select, Stack, Text } from '@mantine/core';

import { SURVEY_TYPE_OPTIONS } from '../../constants/surveyTypes';
import RequestSurveyDueDateFields from './request-survey/RequestSurveyDueDateFields';
import RequestSurveyProductListField from './request-survey/RequestSurveyProductListField';
import RequestSurveyScopeField from './request-survey/RequestSurveyScopeField';
import { requestSurveySelectProps } from './request-survey/requestSurveyForm';
import { useRequestSurveyModal } from './request-survey/useRequestSurveyModal';

type Props = {
    opened: boolean;
    close: () => void;
};

function RequestSurveyModal({ opened, close }: Props) {
    const {
        form,
        isZoneScope,
        activeScopeId,
        submitting,
        listsLoading,
        productsLoading,
        productCount,
        selectedProductListId,
        productListOptions,
        productListPlaceholder,
        availableManagers,
        loadingManagers,
        handleClose,
        handleSubmit,
        handleScopeKindChange,
        handleZoneChange,
        handleStoreChange,
        scopesStillLoading,
        hasZones,
        hasStores,
        availableZones,
        availableStores,
        loadingZones,
        loadingStores,
    } = useRequestSurveyModal(opened, close);

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Request Survey"
            size="lg"
            centered
            classNames={{
                content: 'bg-[#F8F9FA]',
                header: 'bg-[#F8F9FA]',
                title: 'text-[26px] font-bold',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Box className="bg-white rounded-xl p-6">
                    <Stack gap={24}>
                        <Box>
                            <Text size="16px" mb={4}>
                                Survey type
                            </Text>
                            <Select
                                data={SURVEY_TYPE_OPTIONS.map(option => ({
                                    value: option.value,
                                    label: option.label,
                                }))}
                                {...requestSurveySelectProps}
                                {...form.getInputProps('surveyType')}
                            />
                        </Box>

                        <RequestSurveyDueDateFields form={form} />

                        <Box>
                            <RequestSurveyScopeField
                                form={form}
                                isZoneScope={isZoneScope}
                                scopesStillLoading={scopesStillLoading}
                                hasZones={hasZones}
                                hasStores={hasStores}
                                availableZones={availableZones}
                                availableStores={availableStores}
                                loadingZones={loadingZones}
                                loadingStores={loadingStores}
                                onScopeKindChange={handleScopeKindChange}
                                onZoneChange={handleZoneChange}
                                onStoreChange={handleStoreChange}
                            />
                        </Box>

                        <Box>
                            <RequestSurveyProductListField
                                form={form}
                                activeScopeId={activeScopeId}
                                isZoneScope={isZoneScope}
                                listsLoading={listsLoading}
                                productsLoading={productsLoading}
                                productListOptions={productListOptions}
                                selectedProductListId={selectedProductListId}
                                productListPlaceholder={productListPlaceholder}
                                productCount={productCount}
                            />
                        </Box>

                        <Box>
                            <Text size="16px" mb={4}>
                                Champion
                            </Text>
                            <Select
                                required
                                placeholder="Select manager"
                                data={availableManagers}
                                disabled={loadingManagers || !activeScopeId}
                                nothingFoundMessage="No managers found"
                                {...requestSurveySelectProps}
                                {...form.getInputProps('championUserId')}
                            />
                        </Box>

                        <Flex
                            direction="row"
                            justify="center"
                            align="center"
                            gap="md"
                            mt="md"
                        >
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                disabled={submitting}
                                size="md"
                                radius="xl"
                                className="flex-1 max-w-[200px]"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="filled"
                                loading={submitting}
                                disabled={listsLoading || productsLoading}
                                size="md"
                                radius="xl"
                                className="flex-1 max-w-[200px]"
                            >
                                Confirm
                            </Button>
                        </Flex>
                    </Stack>
                </Box>
            </form>
        </Modal>
    );
}

export default RequestSurveyModal;
